import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {createUserWithEmailAndPassword, updateProfile} from 'firebase/auth';
import {doc, setDoc, getDocs, collection} from 'firebase/firestore';
import {auth, db} from '../../firebase';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background-color: #f5f5f5;
  font-family: Arial, sans-serif;
  padding: 20px;
`;

const SetupBox = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  color: #333;
`;

const Description = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
`;

const Button = styled.button<{$loading?: boolean}>`
  padding: 0.75rem;
  background-color: ${props => props.$loading ? '#aaa' : '#2c3e50'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;

  &:hover {
    background-color: ${props => props.$loading ? '#aaa' : '#34495e'};
  }
`;

const Error = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

const Success = styled.div`
  color: #2e7d32;
  background-color: #edf7ed;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

const AdminSetup: React.FC = () => {
  const [credentials, setCredentials] = useState({
    email: 'developer@grema.com',
    password: 'developer123',
    firstName: 'Developer',
    lastName: 'Admin',
    username: 'developer_admin'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adminsExist, setAdminsExist] = useState(false); // New state to track if admins exist
  const navigate = useNavigate();

  React.useEffect(() => {
    const check = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'admins'));
        if (!querySnapshot.empty) {
          setAdminsExist(true);
          // Only redirect if not in development mode or if explicit bypass is not set
          if (process.env.NODE_ENV !== 'development' || process.env.REACT_APP_BYPASS_ADMIN_SETUP_REDIRECT === 'true') {
            // Note: I corrected the logic above to match your intent of bypassing redirect
            if (process.env.NODE_ENV !== 'development' && process.env.REACT_APP_BYPASS_ADMIN_SETUP_REDIRECT !== 'true') {
               navigate('/admin/login');
            }
          } else {
            navigate('/admin/login');
          }
        }
      } catch (err) {
        console.error('Error checking admins:', err);
      }
    };
    check();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      const user = userCredential.user;

      await updateProfile(user, {
        displayName: `${credentials.firstName} ${credentials.lastName}`
      });

      await setDoc(doc(db, 'admins', user.uid), {
        uid: user.uid,
        email: credentials.email,
        firstName: credentials.firstName,
        lastName: credentials.lastName,
        username: credentials.username,
        role: 'admin',
        createdAt: new Date(),
        isActive: true
      });

      setSuccess('Admin account created successfully! You can now login.');
      setTimeout(() => navigate('/admin/login'), 2000);
    } catch (err: any) {
      console.error('Error creating admin:', err);
      let errorMessage = 'Failed to create admin account';
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already exists. Please use a different email.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <SetupBox>
        <Title>Developer Admin Setup</Title>
        <Description>
          {adminsExist && (process.env.NODE_ENV === 'development' && process.env.REACT_APP_BYPASS_ADMIN_SETUP_REDIRECT === 'true')
            ? 'Development mode: Creating/updating developer admin account. Other admins already exist.'
            : 'No admin accounts found. Create a default developer admin account to access the dashboard.'}
        </Description>
        <Form onSubmit={handleCreateAdmin}>
          <InputGroup>
            <Label htmlFor="firstName">First Name</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={credentials.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={credentials.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="Enter email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              placeholder="Enter username"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter password (min 6 characters)"
              required
            />
          </InputGroup>

          {error && <Error>{error}</Error>}
          {success && <Success>{success}</Success>}

          <Button type="submit" $loading={isLoading} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Admin Account'}
          </Button>
        </Form>
      </SetupBox>
    </Container>
  );
};

export default AdminSetup;